import { Breadcrumbs, Typography } from "@material-ui/core"
import { AssetType } from "common/AssetType"
import { CONSTRUCTION } from "common/utils/constants"
import { useTranslations } from "next-intl"
import { BreadcrumbJsonLd } from "next-seo"
import { useRouter } from "next/router"
import { useEffect } from "react"
import styled from "styled-components"
import NextLink from "../../../common/NextLink"

//Styles
const StyledBreadcrumbs = styled(Breadcrumbs)`
  ${(p) => p.theme.breakpoints.down("sm")} {
    padding-top: 20px;
  }
`
const StyledActiveLink = styled(Typography)`
  font-weight: bold;
  color: ${(p) => p.theme.colors.black};
`
type PageBreadcrumbsProps = {
  parentPageType?:
    | "all-assets"
    | "assets-list"
    | "about"
    | "investor-relations"
    | "investments"
  assetType?: AssetType
  thisPageName: string
}

const PageBreadcrumbs = ({
  thisPageName,
  parentPageType,
  assetType,
}: PageBreadcrumbsProps) => {
  const t = useTranslations("breadcrumbs")
  const { asPath } = useRouter()

  useEffect(() => {
    if (window !== undefined) {
      setTimeout(() => {
        const lastLi = document.querySelector("#breadcrumbs li:last-child")
        // add aria-current="page" to the last li
        lastLi?.setAttribute("aria-current", "page")
      })
    }
  }, [])

  const getParentPageUrl = (
    parentPageType:
      | "all-assets"
      | "assets-list"
      | "about"
      | "investments"
      | "investor-relations"
  ): string =>
    parentPageType === "all-assets"
      ? `/assets-list/all-assets`
      : parentPageType === "assets-list"
      ? `/assets-list/offices`
      : parentPageType === "about"
      ? "/about/about-us"
      : parentPageType === "investments"
      ? "/about/about-us"
      : "/investor-relations"

  const breadcrumbArray = [
    {
      position: 1,
      name: t("main"),
      item: "/",
    },
    {
      position: 2,
      name: t(parentPageType),
      item: getParentPageUrl(parentPageType),
    },
    {
      position: 3,
      name: thisPageName,
      item: asPath,
    },
  ]

  if (assetType) {
    const assetItem = {
      position: 3,
      name: t(assetType),
      item: `/assets-list/${assetType}`,
    }

    breadcrumbArray.splice(2, 0, assetItem)
    breadcrumbArray[3].position = 4
  }

  if (assetType === CONSTRUCTION) {
    return (
      <>
        <StyledBreadcrumbs
          id="breadcrumbs"
          separator={"//"}
          aria-label={t("navAria")}
        >
          <NextLink href="/">
            <Typography variant="body2">{t("main")}</Typography>
          </NextLink>
          {thisPageName && (
            <NextLink href="/construction">
              <Typography variant="body2">{t("construction")}</Typography>
            </NextLink>
          )}

          <StyledActiveLink variant="body2">
            {thisPageName || t("construction")}
          </StyledActiveLink>
        </StyledBreadcrumbs>

        <BreadcrumbJsonLd itemListElements={breadcrumbArray} />
      </>
    )
  }

  //Render
  return (
    <>
      <StyledBreadcrumbs
        id="breadcrumbs"
        separator={"//"}
        aria-label={t("navAria")}
      >
        <NextLink href="/">
          <Typography variant="body2">{t("main")}</Typography>
        </NextLink>

        {parentPageType && (
          <NextLink href={getParentPageUrl(parentPageType)}>
            <Typography variant="body2">{t(parentPageType)}</Typography>
          </NextLink>
        )}

        {assetType && (
          <NextLink href={`/assets-list/${assetType}`}>
            <Typography variant="body2">{t(assetType)}</Typography>
          </NextLink>
        )}

        <StyledActiveLink variant="body2">{thisPageName}</StyledActiveLink>
      </StyledBreadcrumbs>

      <BreadcrumbJsonLd itemListElements={breadcrumbArray} />
    </>
  )
}

export default PageBreadcrumbs
